import { IGroupCellRendererParams } from "@ag-grid-community/core";
import { AgGridReactProps } from "@ag-grid-community/react";
import { TransactionWithActiveCellRenderer } from "features/fund-family/setup/fund/txcodes/TransactionCodeActiveCellRenderer";
import { useTransactionCodeConfigListSuspense } from "gen/endpoints/transactions/transactions";
import { IRREffectEnum } from "gen/model/iRREffectEnum";
import { PreferredReturnEffectEnum } from "gen/model/preferredReturnEffectEnum";
import { TransactionCodeConfigData } from "gen/model/transactionCodeConfigData";
import { TransactionCodeConfigUpdateData } from "gen/model/transactionCodeConfigUpdateData";
import { cloneDeep, startCase } from "lodash-es";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import { MaybernTableCheckIconCellRenderer } from "shared/components/table/maybern/MaybernTableCheckIconCellRenderer";
import { maybernTableSuppressKeyboardEventForInput } from "shared/components/table/maybern/MaybernTableSuppressKeyboardEventForInput";
import { enumKeys } from "shared/utils/EnumUtils";

type TxCodeConfigDataGridProps = AgGridReactProps<TransactionCodeConfigData>;

export interface TransactionCodesTableProps {
  fundFamilyId: string;
  isEditable?: boolean;
  onDataChanged?: (data: TransactionCodeConfigUpdateData) => void;
}

export const TransactionCodesTable: React.FC<TransactionCodesTableProps> = ({
  fundFamilyId,
  isEditable,
  onDataChanged,
}) => {
  const { t } = useTranslation(["fundFamily", "enums"]);

  const { data } = useTransactionCodeConfigListSuspense(
    fundFamilyId,
    {
      include_inactive: true,
    },
    {
      query: {
        select: cloneDeep,
      },
    },
  );

  const columnDefs = useMemo<TxCodeConfigDataGridProps["columnDefs"]>(
    () => [
      {
        field: "rich_name",
        headerName: t(
          "fundFamily:setup.transactionCodes.columns.transactionCode",
        ),
        resizable: true,
        checkboxSelection: isEditable,
        showDisabledCheckboxes: isEditable,
        flex: 1,
        minWidth: 400,
        cellClass: "mb-cell-row-header",
        pinned: "left",
        valueFormatter: (params) =>
          params.node?.allChildrenCount ? params.node.key : params.value,
        type: "text_with_active_state",
      },
      {
        field: "preferred_return_reference_cashflow",
        headerName: t(
          "fundFamily:setup.transactionCodes.columns.preferredReturnReferenceCashflow",
        ),
        headerClass: "mb-header-cell-centered",
        ...(isEditable
          ? {
              type: ["dropdown"],
              cellRendererParams: {
                options: enumKeys(PreferredReturnEffectEnum).map((value) => ({
                  label: t(`enums:PreferredReturnEffectEnum.${value}`),
                  value,
                })),
              },
            }
          : {
              valueFormatter: ({ value }) =>
                value
                  ? t(
                      `enums:PreferredReturnEffectEnum.${value as PreferredReturnEffectEnum}`,
                    )
                  : "",
            }),
      },
      {
        field: "is_recallable",
        headerName: t("fundFamily:setup.transactionCodes.columns.isRecallable"),
        type: "checkbox",
      },
      {
        field: "affects_waterfall",
        headerName: t(
          "fundFamily:setup.transactionCodes.columns.affectsWaterfall",
        ),
        type: "checkbox",
      },
      {
        field: "affects_equalization_interest",
        headerName: t(
          "fundFamily:setup.transactionCodes.columns.affectsEqualizationInterest",
        ),
        type: "checkbox",
      },
      {
        field: "affects_fee_accrual_interest",
        headerName: t(
          "fundFamily:setup.transactionCodes.columns.affectsFeeAccrualInterest",
        ),
        type: "checkbox",
      },
      {
        field: "affects_gross_irr",
        headerName: t(
          "fundFamily:setup.transactionCodes.columns.affectsGrossIrr",
        ),
        headerClass: "mb-header-cell-centered",
        ...(isEditable
          ? {
              type: ["dropdown"],
              cellRendererParams: {
                options: enumKeys(IRREffectEnum).map((value) => ({
                  label: t(`enums:IRREffectEnum.${value}`),
                  value,
                })),
              },
            }
          : {
              valueFormatter: ({ value }) =>
                value ? t(`enums:IRREffectEnum.${value as IRREffectEnum}`) : "",
            }),
      },
      {
        field: "affects_net_irr",
        headerName: t(
          "fundFamily:setup.transactionCodes.columns.affectsNetIrr",
        ),
        headerClass: "mb-header-cell-centered",
        ...(isEditable
          ? {
              type: ["dropdown"],
              cellRendererParams: {
                options: enumKeys(IRREffectEnum).map((value) => ({
                  label: t(`enums:IRREffectEnum.${value}`),
                  value,
                })),
              },
            }
          : {
              valueFormatter: ({ value }) =>
                value ? t(`enums:IRREffectEnum.${value as IRREffectEnum}`) : "",
            }),
      },
      {
        field: "affects_net_asset_value",
        headerName: t(
          "fundFamily:setup.transactionCodes.columns.affectsNetAssetValue",
        ),
        type: "checkbox",
      },
      {
        field: "classification",
        initialRowGroup: true,
        initialHide: true,
        keyCreator: ({ data }) =>
          data.classification
            ? t("fundFamily:setup.transactionCodes.rowGroupTitle", {
                name: startCase(data.classification),
              })
            : "",
      },
    ],
    [t, isEditable],
  );

  const partialUpdateTransactionCodeConfig = (
    data: TransactionCodeConfigData,
    fieldsToUpdate: Partial<TransactionCodeConfigUpdateData>,
  ) => {
    const newData: TransactionCodeConfigUpdateData = {
      ...(data as TransactionCodeConfigUpdateData),
      ...fieldsToUpdate,
    };
    onDataChanged?.(newData);
  };

  return (
    <MaybernTable
      columnTypes={columnTypes}
      columnDefs={columnDefs}
      rowData={data}
      getRowHeight={getRowHeight}
      initialGroupOrderComparator={initialGroupOrderComparator}
      rowSelection="multiple"
      suppressRowClickSelection={true}
      suppressCopyRowsToClipboard={true}
      groupDisplayType="groupRows"
      groupAllowUnbalanced={true}
      groupDefaultExpanded={1}
      groupRowRendererParams={groupRowRendererParams}
      getRowId={(params) => params.data.id!}
      defaultColDef={{
        cellRendererParams: {
          disabled: !isEditable,
        },
        suppressKeyboardEvent: maybernTableSuppressKeyboardEventForInput,
      }}
      onGridReady={(params) => {
        params.api.forEachNode((node) => {
          node.setSelected(node.data?.state === "active", undefined, "api");
        });
      }}
      onRowSelected={(params) => {
        if (params.source !== "api") {
          partialUpdateTransactionCodeConfig(params.node.data!, {
            state: params.node.isSelected() ? "active" : "inactive",
          });
        }
      }}
      onCellValueChanged={(params) => {
        if (!params.node.isSelected()) {
          params.node.setSelected(true);
        }
        partialUpdateTransactionCodeConfig(params.node.data!, {
          [params.colDef.field!]: params.newValue,
        });
      }}
      context={{ isEditable }}
      sx={{
        ".ag-header-cell, .ag-cell": {
          px: "16px",
          borderRightWidth: "1px",
          borderBottomWidth: "1px",
          borderTopWidth: "0px",
          borderLeftWidth: "0px",
          borderColor: "gray.100",
          borderStyle: "solid",
        },
        ".ag-row-group": {
          bg: "teal.25",
          textStyle: "smallBold",
          color: "gray.700",
          "--ag-cell-horizontal-padding": "24px",
          fontSize: "14px",
          fontWeight: "normal",
        },
        ".ag-cell-wrapper": {
          // Hide row group expand/collapse arrows
          ".ag-group-expanded, .ag-group-contracted": {
            display: "none",
          },
        },
        ".ag-row:not(.ag-row-selected)": {
          color: "gray.400",
          "--ag-background-color": "var(--chakra-colors-gray-25)",
          backgroundColor: "var(--ag-background-color)",
        },
      }}
    />
  );
};

interface TransactionCodeConfigTableContext {
  isEditable: boolean;
}

const columnTypes: TxCodeConfigDataGridProps["columnTypes"] = {
  checkbox: {
    cellClass: "mb-cell-value-type-boolean",
    headerClass: "mb-header-cell-centered",
    width: 150,
    valueFormatter: (params) => (params.value ? "\u2713" : "-"),
    cellRendererSelector: ({ context }) => {
      const { isEditable } = context as TransactionCodeConfigTableContext;
      return {
        component: isEditable
          ? "agCheckboxCellRenderer"
          : MaybernTableCheckIconCellRenderer,
      };
    },
  },
  text_with_active_state: {
    width: 150,
    cellRendererSelector: () => ({
      component: TransactionWithActiveCellRenderer,
    }),
  },
};

const groupRowRendererParams: IGroupCellRendererParams = {
  suppressCount: true,
  suppressDoubleClickExpand: true,
  suppressEnterExpand: true,
};

const getRowHeight: TxCodeConfigDataGridProps["getRowHeight"] = () => 44;

const initialGroupOrderComparator: TxCodeConfigDataGridProps["initialGroupOrderComparator"] =
  ({ nodeA, nodeB }) => {
    // Sort groups below ungrouped
    if (nodeA.group && !nodeB.group) {
      return 1;
    } else if (!nodeA.group && nodeB.group) {
      return -1;
    }

    // Sort groups
    if (nodeA.group && nodeB.group && nodeA.key && nodeB.key) {
      return nodeA.key.localeCompare(nodeB.key);
    }

    // Sort ungrouped
    if (nodeA.data?.rich_name && nodeB.data?.rich_name) {
      return nodeA.data.rich_name.localeCompare(nodeB.data.rich_name);
    }

    return 0;
  };
